<template>
    <div
        v-if="!pending && chapters && chapters.length"
        class="guideline-chapters-mobile"
        :class="{ 'is-expanded': isExpanded }"
    >
        <!-- Collapsed state: show active chapter with toggle -->
        <button
            class="guideline-chapters-mobile__header"
            type="button"
            @click="isExpanded = !isExpanded"
        >
            <span class="guideline-chapters-mobile__header-title">
                {{ activeChapter ? `${activeChapterIndex + 1}. ${titleCase(activeChapter.title)}` : 'Select chapter' }}
            </span>
            <BaseIcon
                class="guideline-chapters-mobile__header-icon"
                :icon="isExpanded ? 'chevron-up' : 'chevron-down'"
            />
        </button>

        <!-- Expanded state: show all chapters -->
        <component
            :is="numeric ? 'ol' : 'ul'"
            v-show="isExpanded"
            class="guideline-chapters-mobile__list"
        >
            <li
                v-for="(chapter, index) in chapters"
                :key="index"
                :class="[
                    'guideline-chapters-mobile__item',
                    { 'is-active': isChapterActive(chapter, index) }
                ]"
            >
                <a
                    v-if="chapter.uri && chapter.title && chapter.isAnchor"
                    class="guideline-chapters-mobile__title"
                    :href="chapter.uri"
                    @click.prevent="handleChapterClick(chapter.uri, true)"
                >
                    {{ titleCase(chapter.title) }}
                </a>

                <nuxt-link
                    v-else-if="chapter.uri && chapter.title"
                    :to="`/${chapter.uri}`"
                    class="guideline-chapters-mobile__title"
                    @click="isExpanded = false"
                >
                    {{ titleCase(chapter.title) }}
                </nuxt-link>

                <BasePill
                    v-if="getHighlightCountBySlug(chapter.slug)"
                    element="span"
                    class="pill--result"
                >
                    <template v-if="getHighlightCountBySlug(chapter.slug) > 1">
                        {{ getHighlightCountBySlug(chapter.slug) }} results
                    </template>
                    <template v-else>
                        1 result
                    </template>
                </BasePill>

                <ol
                    v-if="getSubheadingsBySlug(chapter.slug).length"
                    class="guideline-chapters-mobile__sublist"
                >
                    <li
                        v-for="(subheading, subIndex) in getSubheadingsBySlug(chapter.slug)"
                        :key="subIndex"
                        class="guideline-chapters-mobile__subitem"
                    >
                        <a
                            v-if="subheading.anchor && subheading.title"
                            class="guideline-chapters-mobile__subtitle"
                            @click.prevent="handleSubheadingClick(chapter, subheading.anchor)"
                        >
                            <span class="guideline-chapters-mobile__subtitle-number">{{ parseSubheadingTitle(subheading.title).number }}</span>
                            <span class="guideline-chapters-mobile__subtitle-text">{{ titleCase(parseSubheadingTitle(subheading.title).text) }}</span>
                        </a>
                    </li>
                </ol>
            </li>
        </component>
    </div>

    <content-placeholders
        v-else-if="pending"
        class="guideline-chapters-mobile"
    >
        <div
            v-for="i in [1,2,3,4,5,6]"
            :key="i"
            class="guideline-chapters-mobile__item"
        >
            <content-placeholders-text :lines="1" />
        </div>
    </content-placeholders>
</template>


<script setup>
import { useGuidelineSearchStore } from '~/store/guideline-search';
import { useRoute } from 'vue-router';
import BaseIcon from '~/components/BaseIcon/BaseIcon.vue';

const route = useRoute();

const guidelineSearchStore = useGuidelineSearchStore();

const isExpanded = ref(false);

const props = defineProps({
    pending: {
        type: Boolean,
        default: false,
        required: false
    },

    numeric: {
        type: Boolean,
        default: true,
        required: false
    },

    chapters: {
        type: Array,
        required: false,
        default: null
    },
    applyTitleCase: {
        type: Boolean,
        required: false,
        default: true
    }
});

function isChapterActive(chapter, index) {
    return (
        `/${chapter.uri}` === route.path ||
        (
            index === 0 &&
            (route.name === 'guideline-slug' || route.name === 'guideline-summary')
        )
    );
}

const activeChapter = computed(() => {
    if (!props.chapters?.length) {
        return null;
    }

    const activeIndex = props.chapters.findIndex((chapter, index) => isChapterActive(chapter, index));
    return activeIndex >= 0 ? props.chapters[activeIndex] : props.chapters[0];
});

const activeChapterIndex = computed(() => {
    if (!props.chapters?.length) {
        return -1;
    }

    return props.chapters.findIndex((chapter, index) =>
        isChapterActive(chapter, index)
    );
});

const { chapters: storeChapters } = storeToRefs(guidelineSearchStore);

function getSubheadingsBySlug(chapterSlug) {
    const subheadings = guidelineSearchStore.getSubheadingsBySlug(chapterSlug);
    if (!subheadings || subheadings.length <= 1) {
        return [];
    }
    return subheadings.slice(1);
}

/**
 * Parse a subheading title into number prefix and text
 * e.g. "3.2. Aetiology And Risk Factors" => { number: "3.2.", text: "Aetiology And Risk Factors" }
 */
function parseSubheadingTitle(title) {
    const match = title?.match(/^([\d.]+\.?)\s*(.*)$/);
    if (match) {
        return { number: match[1], text: match[2] };
    }
    return { number: '', text: title || '' };
}

function titleCase(str) {
    if (typeof str !== 'string') {
        return str;
    }
    if (props.applyTitleCase) {
        return str.toLowerCase().split(' ').map(function(word) {
            if (!word) {
                return '';
            }
            return word.replace(word[0], word[0].toUpperCase());
        }).join(' ');
    } else {
        return str;
    }
}

function getHighlightCountBySlug(chapterSlug) {
    const chapterMeta = storeChapters.value?.find(chapter => chapter.slug === chapterSlug);

    if (!chapterMeta) {
        return;
    }

    return chapterMeta.highlightCount;
}

function goToAnchor(id) {
    const cleanId = id.replace('#', '');
    const el = document.getElementById(cleanId);

    if (!el) {return;}

    const offset = -150;
    const elPositionY = el.offsetTop;

    window.scrollTo({ behavior: 'smooth', top: elPositionY + offset });
}

async function goToSubheading(chapter, anchor) {
    const chapterPath = `/${chapter.uri}`;
    const isOnChapter = route.path === chapterPath;

    if (isOnChapter) {
        // Already on the chapter, just scroll to anchor
        goToAnchor(anchor);
    } else {
        // Navigate to chapter with anchor hash - chapter.vue handles scroll after load
        await navigateTo({ path: chapterPath, hash: anchor });
    }
}

function handleChapterClick(uri, isAnchor) {
    if (isAnchor) {
        goToAnchor(uri);
    }
    isExpanded.value = false;
}

async function handleSubheadingClick(chapter, anchor) {
    await goToSubheading(chapter, anchor);
    isExpanded.value = false;
}
</script>

<style lang="less">
.guideline-chapters-mobile {
    display: block;
    margin: 0;
    background: var(--color-gray-200);

    @media @q-md-min {
        display: none;
    }
}

.guideline-chapters-mobile__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: .875rem 1.4375rem; //14px 23px in rem
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;

    .guideline-chapters-mobile.is-expanded & {
        border-bottom: 1px solid var(--color-black-10);
    }
}

.guideline-chapters-mobile__header-title {
    .typography-label();
    font-weight: 700;
    color: var(--color-primary);
}

.guideline-chapters-mobile__header-icon {
    font-size: 1.5rem;
    flex-shrink: 0;
    transition: transform .2s ease;
}

.guideline-chapters-mobile__list {
    max-height: calc(100svh - 5rem - 3.125rem); // Full viewport height minus the menu heading and spacing
    overflow: auto;
    margin: 0;
    padding: .25rem 1rem;
    overscroll-behavior: contain;
}

ul.guideline-chapters-mobile__list {
    li:before {
        content: none;
        display: none;
    }

    li {
        padding-left: 0;
    }
}

.guideline-chapters-mobile__item {
    position: relative;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    margin: 0;
    padding: 0 0 0 2rem;
    line-height: 1;
    border-bottom: 1px solid var(--color-black-10);

    &:last-child {
        border-bottom: 0;
    }

    a, &:before {
        font-size: .875rem;
        line-height: 1.3;
        color: var(--color-secondary-dark);
        text-decoration: none;
        opacity: .8;
    }

    .vue-content-placeholders-text,
    .guideline-chapters-mobile__title {
        flex: 1 1 auto;
        transition: opacity var(--transition-fast);
        line-height: 1.3;

        padding-top: 1rem;
        padding-bottom: 1rem;
    }

    .guideline-chapters-mobile__subtitle {
        padding-top: .5rem;
        padding-bottom: .5rem;
    }

    .pill {
        white-space: nowrap;
        justify-self: flex-end;
        align-self: center;
    }

    &:active,
    &:hover {
        a, &:before {
            opacity: 1;
        }
    }

    &:active {
        a, &:before {
            color: var(--color-primary);
        }
    }

    &:before {
        top: auto;
        left: .85rem;
        line-height: 1.4;
        padding-top: 1rem;
        padding-bottom: 1rem;
    }
}

.guideline-chapters-mobile__title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.guideline-chapters-mobile__item.is-active {
    &:before {
        font-weight: 700;
        color: var(--color-primary);
        opacity: 1;
    }

    > .guideline-chapters-mobile__title {
        font-weight: 700;
        color: var(--color-primary);
        opacity: 1;
    }
}

.guideline-chapters-mobile__sublist {
    margin-top: 0;
    margin-bottom: 1rem;
    list-style: none;
}

.guideline-chapters-mobile__sublist li:before {
    content: none;
}

li.guideline-chapters-mobile__subitem {
    padding-left: 0;
    cursor: pointer;
    margin-bottom: 0;
}

.guideline-chapters-mobile__subtitle {
    display: flex;
    gap: 0.5rem;
    color: var(--color-secondary-dark);
}

.guideline-chapters-mobile__subtitle-number {
    width: 4ch;
    flex-shrink: 0;
}

.guideline-chapters-mobile__subtitle-text {
    flex: 1;
}
</style>
